import { useEffect, useMemo, useRef, useState } from 'react';
import { PutObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { ArrowRight, ChevronLeft, ChevronRight, RefreshCw, SkipForward, Square, Star, UploadCloud } from 'lucide-react';
import FalconSmileAppointmentProposal from './FalconSmile.appointment-proposal';

const AWS_REGION = 'us-west-2';
const S3_BUCKET = 'dentalive-review-images';
const AWS_ACCESS_KEY_ID = 'AKIAVPXRX2JHWVVZ3Y6X';
const AWS_SECRET_ACCESS_KEY = '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';
const S3_PUBLIC_BASE_URL = `https://${S3_BUCKET}.s3.amazonaws.com`;

const s3Client = new S3Client({
  region: AWS_REGION,
  credentials: {
    accessKeyId: AWS_ACCESS_KEY_ID,
    secretAccessKey: AWS_SECRET_ACCESS_KEY,
  },
});

const getJsonKey = (userUuid) => `JSON/${userUuid}.json`;
const getUsersKey = () => 'users.json';
const createUuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const segment = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(-4);
  return `${segment()}${segment()}-${segment()}-${segment()}-${segment()}-${segment()}${segment()}${segment()}`;
};

const safeParseJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const createEmptyReviewList = () => [];
const createEmptyUsersRegistry = () => ({});

const normalizeUsernameKey = (value) => String(value || '').trim().toLowerCase();
const normalizeUsernameLabel = (value) => String(value || '').trim();
const buildPublicS3Url = (key) => `${S3_PUBLIC_BASE_URL}/${String(key || '').split('/').map(encodeURIComponent).join('/')}`;

const resolveSavedImageUrl = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';
  if (rawValue.startsWith('data:')) return rawValue;
  if (/^https?:\/\//i.test(rawValue)) {
    try {
      const url = new URL(rawValue);
      url.search = '';
      url.hash = '';
      return url.href;
    } catch {
      return rawValue.split('?')[0].split('#')[0];
    }
  }
  return `${S3_PUBLIC_BASE_URL}/${rawValue.replace(/^\/+/, '')}`;
};

const findRegistryUser = (registry, username) => {
  const usernameKey = normalizeUsernameKey(username);
  if (!usernameKey || !registry || typeof registry !== 'object') return null;

  const directMatch = registry[usernameKey];
  if (directMatch) return { key: usernameKey, entry: directMatch };

  for (const [key, entry] of Object.entries(registry)) {
    if (normalizeUsernameKey(key) === usernameKey || normalizeUsernameKey(entry?.username) === usernameKey) {
      return { key: normalizeUsernameKey(key) || usernameKey, entry };
    }
  }

  return null;
};

const saveJsonArray = async (key, value) => {
  await s3Client.send(
    new PutObjectCommand({
      Bucket: S3_BUCKET,
      Key: key,
      Body: JSON.stringify(value, null, 2),
      ContentType: 'application/json',
      CacheControl: 'no-store, no-cache, max-age=0, must-revalidate',
      Expires: new Date(0).toUTCString(),
    }),
  );
};

const createReviewRecord = async (userUuid) => {
  const record = createEmptyReviewList();
  await saveJsonArray(getJsonKey(userUuid), record);
  return record;
};

const createUsersRecord = async () => {
  const record = createEmptyUsersRegistry();
  await saveJsonArray(getUsersKey(), record);
  return record;
};

const normalizeReviewList = (parsed) => {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object' && Array.isArray(parsed.analyses)) {
    return parsed.analyses;
  }
  return createEmptyReviewList();
};

const normalizeUsersRegistry = (parsed) => {
  if (!parsed) return createEmptyUsersRegistry();

  if (Array.isArray(parsed)) {
    return parsed.reduce((registry, entry) => {
      const usernameKey = normalizeUsernameKey(entry?.username || entry?.user_name || entry?.user_id);
      if (!usernameKey) return registry;

      registry[usernameKey] = {
        username: normalizeUsernameLabel(entry?.username || entry?.user_name || entry?.user_id),
        password: String(entry?.password || ''),
        user_uuid: entry?.user_uuid || entry?.uuid || createUuid(),
        review_json_key: entry?.review_json_key || getJsonKey(entry?.user_uuid || entry?.uuid || ''),
        review_count: Number(entry?.review_count || 0),
        created_at: entry?.created_at || new Date().toISOString(),
        updated_at: entry?.updated_at || new Date().toISOString(),
      };
      return registry;
    }, {});
  }

  if (parsed && typeof parsed === 'object') {
    if (parsed.users && typeof parsed.users === 'object' && !Array.isArray(parsed.users)) {
      return Object.fromEntries(
        Object.entries(parsed.users).map(([username, entry]) => {
          const usernameKey = normalizeUsernameKey(username);
          return [usernameKey, {
            username: normalizeUsernameLabel(entry?.username || username),
            password: String(entry?.password || ''),
            user_uuid: entry?.user_uuid || entry?.uuid || createUuid(),
            review_json_key: entry?.review_json_key || getJsonKey(entry?.user_uuid || entry?.uuid || ''),
            review_count: Number(entry?.review_count || 0),
            created_at: entry?.created_at || new Date().toISOString(),
            updated_at: entry?.updated_at || new Date().toISOString(),
          }];
        }),
      );
    }

    return Object.fromEntries(
      Object.entries(parsed).map(([username, entry]) => {
        const usernameKey = normalizeUsernameKey(username);
        return [usernameKey, {
          username: normalizeUsernameLabel(entry?.username || username),
          password: String(entry?.password || ''),
          user_uuid: entry?.user_uuid || entry?.uuid || createUuid(),
          review_json_key: entry?.review_json_key || getJsonKey(entry?.user_uuid || entry?.uuid || ''),
          review_count: Number(entry?.review_count || 0),
          created_at: entry?.created_at || new Date().toISOString(),
          updated_at: entry?.updated_at || new Date().toISOString(),
        }];
      }),
    );
  }

  return createEmptyUsersRegistry();
};

const loadReviewRecord = async (userUuid) => {
  const key = getJsonKey(userUuid);
  try {
    const response = await fetch(`${buildPublicS3Url(key)}?v=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) {
      if (response.status === 404) {
        return createReviewRecord(userUuid);
      }
      throw new Error(`Unable to load review JSON (${response.status})`);
    }

    const parsed = safeParseJson(await response.text());
    return normalizeReviewList(parsed);
  } catch (error) {
    const missingObject = error?.status === 404 || /404/.test(String(error?.message || ''));
    if (missingObject) {
      return createReviewRecord(userUuid);
    }

    if (!missingObject) {
      console.error('Unable to load review JSON', error);
    }
  }

  return createEmptyReviewList();
};

const loadUsersRecord = async () => {
  const key = getUsersKey();
  try {
    const response = await fetch(`${buildPublicS3Url(key)}?v=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) {
      if (response.status === 404) {
        return createUsersRecord();
      }
      throw new Error(`Unable to load users.json (${response.status})`);
    }

    const parsed = safeParseJson(await response.text());
    return normalizeUsersRegistry(parsed);
  } catch (error) {
    const missingObject = error?.status === 404 || /404/.test(String(error?.message || ''));
    if (missingObject) {
      return createUsersRecord();
    }

    if (!missingObject) {
      console.error('Unable to load users.json', error);
    }
  }

  return createEmptyUsersRegistry();
};

const FalconSmileReview = ({ config = {} }) => {
  const params = new URLSearchParams(window.location.search);
  const apiKey = config.apiKey || params.get('apiKey') || params.get('api-key') || '';
  const parentOrigin = config.parentOrigin || params.get('parentOrigin') || window.location.origin;

  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [username, setUsername] = useState('');
  const [userUuid, setUserUuid] = useState('');
  const [isStarted, setIsStarted] = useState(false);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);
  const [reviewEntries, setReviewEntries] = useState([]);
  const [usersIndex, setUsersIndex] = useState({});
  const [analysisData, setAnalysisData] = useState(null);
  const [imageRating, setImageRating] = useState('');
  const [assessmentRating, setAssessmentRating] = useState('');
  const [imageFeedback, setImageFeedback] = useState('');
  const [assessmentFeedback, setAssessmentFeedback] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [roundKey, setRoundKey] = useState(0);
  const [activeTab, setActiveTab] = useState('main');
  const [visibleHistoryCount, setVisibleHistoryCount] = useState(4);
  const historyLoadMoreRef = useRef(null);
  const [viewerImage, setViewerImage] = useState(null);
  const [mobileCarouselKeys, setMobileCarouselKeys] = useState({});
  const [editingAnalysisId, setEditingAnalysisId] = useState('');
  const [editingDraft, setEditingDraft] = useState(null);

  const currentAnalysis = useMemo(() => {
    if (!analysisData) return null;

    const alternatives = Array.isArray(analysisData.alternatives)
      ? analysisData.alternatives.filter(Boolean).slice(0, 2)
      : [];

    return {
      originalImage: analysisData.originalImage || '',
      originalImageUrl: analysisData.originalImageUrl || analysisData.originalImage || '',
      alternatives,
      triage: analysisData.triage || null,
    };
  }, [analysisData]);

  const reviewedEntries = useMemo(() => reviewEntries.filter((entry) => {
    return Boolean(
      entry?.original_image_url ||
      entry?.alternative_image_url_1 ||
      entry?.alternative_image_url_2 ||
      entry?.image_rating ||
      entry?.assessment_rating ||
      entry?.image_feedback ||
      entry?.assessment_feedback,
    );
  }), [reviewEntries]);

  const getReviewSortTime = (entry) => {
    const rawValue = entry?.updated_at || entry?.created_at || '';
    const parsedValue = Date.parse(rawValue);
    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  };

  const sortedReviewedEntries = useMemo(() => (
    [...reviewedEntries].sort((leftEntry, rightEntry) => getReviewSortTime(rightEntry) - getReviewSortTime(leftEntry))
  ), [reviewedEntries]);

  useEffect(() => {
    setVisibleHistoryCount(Math.min(4, sortedReviewedEntries.length || 4));
  }, [sortedReviewedEntries.length]);

  const historyDisplayEntries = sortedReviewedEntries.slice(0, visibleHistoryCount);

  const normalizeTriageAnalysis = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const getEntryAssessment = (entry) => (
    normalizeTriageAnalysis(entry?.triage_analysis || entry?.triage || entry?.assessment)
  );

  const getAssessmentSpecialists = (assessment) => {
    const specialists = Array.isArray(assessment?.specialists) ? assessment.specialists : [];
    return specialists.filter(Boolean);
  };

  useEffect(() => {
    if (!isStarted) return;
    if (activeTab !== 'history') return;

    const observerTarget = historyLoadMoreRef.current;
    if (!observerTarget) return;

    const hasMore = visibleHistoryCount < sortedReviewedEntries.length;
    if (!hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setVisibleHistoryCount((value) => Math.min(value + 4, sortedReviewedEntries.length));
    }, { rootMargin: '200px 0px' });

    observer.observe(observerTarget);
    return () => observer.disconnect();
  }, [activeTab, isStarted, sortedReviewedEntries.length, visibleHistoryCount]);

  useEffect(() => {
    if (!viewerImage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setViewerImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewerImage]);

  const renderStars = (value, onChange) => (
    <div className="mt-2 flex flex-wrap gap-2">
      {Array.from({ length: 5 }, (_, index) => index + 1).map((rating) => {
        const selected = rating <= Number(value || 0);

        return (
          <button
            key={rating}
            type="button"
            onClick={() => onChange(String(rating))}
            aria-label={`${rating} star${rating === 1 ? '' : 's'}`}
            className={`inline-flex h-12 w-12 items-center justify-center rounded-full border transition ${selected
              ? 'border-amber-400 bg-amber-50 text-amber-500 shadow-sm'
              : 'border-slate-200 bg-slate-50 text-slate-300 hover:border-amber-200 hover:text-amber-300'
              }`}
          >
            <Star size={22} fill={selected ? 'currentColor' : 'none'} strokeWidth={1.8} />
          </button>
        );
      })}
    </div>
  );

  const renderStaticStars = (value) => (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {Array.from({ length: 5 }, (_, index) => index + 1).map((rating) => {
        const selected = rating <= Number(value || 0);

        return (
          <span
            key={rating}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-full border ${selected
              ? 'border-amber-400 bg-amber-50 text-amber-500'
              : 'border-slate-200 bg-slate-50 text-slate-300'
              }`}
            aria-hidden="true"
          >
            <Star size={18} fill={selected ? 'currentColor' : 'none'} strokeWidth={1.8} />
          </span>
        );
      })}
    </div>
  );

  const openViewer = ({ images = [], initialKey = 'original' }) => {
    const normalizedImages = images
      .map((item) => ({
        key: item.key,
        label: item.label,
        src: item.src,
      }))
      .filter((item) => Boolean(item.src));

    if (!normalizedImages.length) return;

    const initialImage = normalizedImages.find((item) => item.key === initialKey) || normalizedImages[0];
    const secondImage = normalizedImages.find((item) => item.key !== initialImage.key) || normalizedImages[0];
    const thirdImage = normalizedImages.find((item) => item.key !== initialImage.key && item.key !== secondImage.key)
      || normalizedImages[0];

    setViewerImage({
      mode: 'single',
      images: normalizedImages,
      singleKey: initialImage.key,
      leftKey: initialImage.key,
      rightKey: secondImage.key,
      thirdKey: thirdImage.key,
    });
  };

  const closeViewer = () => setViewerImage(null);

  const updateViewerSlot = (slot, key) => {
    if (!viewerImage) return;
    const nextImage = viewerImage.images.find((item) => item.key === key);
    if (!nextImage) return;
    setViewerImage((current) => {
      if (!current) return current;
      const nextState = { ...current, [`${slot}Key`]: key };
      if (slot === 'single') {
        nextState.leftKey = key;
        nextState.rightKey = current.rightKey || current.images.find((item) => item.key !== key)?.key || key;
        nextState.thirdKey = current.images.find((item) => item.key !== key && item.key !== nextState.rightKey)?.key || key;
      }
      return nextState;
    });
  };

  const setViewerMode = (mode) => {
    if (!viewerImage) return;
    setViewerImage((current) => {
      if (!current) return current;
      const fallbackFirst = current.images[0]?.key || 'original';
      const fallbackSecond = current.images.find((item) => item.key !== fallbackFirst)?.key || fallbackFirst;
      const fallbackThird = current.images.find((item) => item.key !== fallbackFirst && item.key !== fallbackSecond)?.key || fallbackFirst;
      const selectedSingle = current.singleKey || fallbackFirst;
      return {
        ...current,
        mode,
        singleKey: selectedSingle,
        leftKey: current.leftKey || selectedSingle || fallbackFirst,
        rightKey: current.rightKey || fallbackSecond,
        thirdKey: current.thirdKey || fallbackThird,
      };
    });
  };

  const getViewerItem = (key) => viewerImage?.images?.find((item) => item.key === key) || null;

  const getFirstAvailableImageKey = (images = []) => images.find((item) => item.src)?.key || images[0]?.key || '';

  const getSelectedCarouselImage = (images = [], selectedKey) => (
    images.find((item) => item.key === selectedKey && item.src) || images.find((item) => item.src) || null
  );

  const updateMobileCarouselKey = (carouselKey, imageKey) => {
    setMobileCarouselKeys((current) => ({ ...current, [carouselKey]: imageKey }));
  };

  const stepMobileCarousel = (carouselKey, images = [], direction) => {
    const availableImages = images.filter((item) => item.src);
    if (!availableImages.length) return;
    const selectedKey = mobileCarouselKeys[carouselKey] || getFirstAvailableImageKey(availableImages);
    const currentIndex = Math.max(0, availableImages.findIndex((item) => item.key === selectedKey));
    const nextIndex = (currentIndex + direction + availableImages.length) % availableImages.length;
    updateMobileCarouselKey(carouselKey, availableImages[nextIndex].key);
  };

  const renderViewerPicker = (slotTitle, selectedKey, onChange) => (
    <div className="rounded-[1.4rem] border border-white/10 bg-white/90 p-4 text-slate-900 shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
      <p className="text-sm font-black uppercase tracking-[0.22em] text-slate-500">{slotTitle}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {viewerImage?.images?.map((item) => (
          <label
            key={`${slotTitle}-${item.key}`}
            className={`flex min-w-[132px] flex-1 cursor-pointer items-center justify-center gap-2 rounded-[999px] border px-3 py-2 transition ${selectedKey === item.key
              ? 'border-cyan-400 bg-cyan-50'
              : 'border-slate-200 bg-white hover:border-cyan-200 hover:bg-slate-50'
              }`}
          >
            <input
              type="radio"
              name={slotTitle}
              value={item.key}
              checked={selectedKey === item.key}
              onChange={() => onChange(item.key)}
              className="h-4 w-4 accent-cyan-500"
            />
            <span className="text-sm font-semibold text-slate-800">{item.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const resetEditingState = () => {
    setEditingAnalysisId('');
    setEditingDraft(null);
  };

  const createReviewPayload = (overrides = {}) => ({
    analysis_id: overrides.analysis_id || createUuid(),
    created_at: overrides.created_at || new Date().toISOString(),
    updated_at: overrides.updated_at || null,
    user_uuid: overrides.user_uuid || userUuid,
    username: overrides.username || username,
    original_image_url: overrides.original_image_url || currentAnalysis?.originalImageUrl || '',
    alternative_image_url_1: overrides.alternative_image_url_1 || currentAnalysis?.alternatives?.[0] || '',
    alternative_image_url_2: overrides.alternative_image_url_2 || currentAnalysis?.alternatives?.[1] || '',
    image_rating: Number.isInteger(Number(overrides.image_rating)) ? Number(overrides.image_rating) : Number(imageRating),
    assessment_rating: Number.isInteger(Number(overrides.assessment_rating)) ? Number(overrides.assessment_rating) : Number(assessmentRating),
    image_feedback: typeof overrides.image_feedback === 'string' ? overrides.image_feedback : imageFeedback.trim(),
    assessment_feedback: typeof overrides.assessment_feedback === 'string' ? overrides.assessment_feedback : assessmentFeedback.trim(),
    triage_analysis: overrides.triage_analysis ?? currentAnalysis?.triage ?? null,
  });

  const validateRatings = (imageValue, assessmentValue) => {
    const numericImageRating = Number(imageValue);
    const numericAssessmentRating = Number(assessmentValue);

    if (!Number.isInteger(numericImageRating) || numericImageRating < 1 || numericImageRating > 5) {
      return 'Please choose an image rating from 1 to 5 stars.';
    }
    if (!Number.isInteger(numericAssessmentRating) || numericAssessmentRating < 1 || numericAssessmentRating > 5) {
      return 'Please choose an assessment rating from 1 to 5 stars.';
    }

    return '';
  };

  const startReview = async () => {
    const trimmedUsername = normalizeUsernameLabel(usernameInput);
    const trimmedPassword = String(passwordInput || '').trim();

    if (!trimmedUsername) {
      setError('Please enter a username before starting.');
      return;
    }
    if (!trimmedPassword) {
      setError('Please enter a password before starting.');
      return;
    }

    const usernameKey = normalizeUsernameKey(trimmedUsername);

    setError('');
    setIsLoadingRecord(true);

    try {
      const loadedUsers = await loadUsersRecord();
      const matchedUser = findRegistryUser(loadedUsers, trimmedUsername);
      const existingUser = matchedUser?.entry || null;
      const existingUserKey = matchedUser?.key || usernameKey;

      if (existingUser && String(existingUser.password || '') !== trimmedPassword) {
        setError('That username already exists. Please enter the correct password.');
        return;
      }

      const nextUser = existingUser
        ? { ...existingUser }
        : {
          username: trimmedUsername,
          password: trimmedPassword,
          user_uuid: createUuid(),
          review_json_key: '',
          review_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      nextUser.username = trimmedUsername;
      if (!existingUser) {
        nextUser.password = trimmedPassword;
      }
      nextUser.user_uuid = nextUser.user_uuid || createUuid();
      const expectedReviewJsonKey = getJsonKey(nextUser.user_uuid);
      if (!nextUser.review_json_key || nextUser.review_json_key !== expectedReviewJsonKey) {
        nextUser.review_json_key = expectedReviewJsonKey;
      }
      nextUser.updated_at = new Date().toISOString();

      const reviewRecord = await loadReviewRecord(nextUser.user_uuid);
      setReviewEntries(reviewRecord);
      setReviewCount(reviewRecord.length);

      const normalizedUser = {
        ...nextUser,
        review_count: reviewRecord.length,
        updated_at: new Date().toISOString(),
      };
      const syncedUsersIndex = {
        ...loadedUsers,
        [existingUserKey]: normalizedUser,
      };
      if (existingUserKey !== usernameKey) {
        delete syncedUsersIndex[usernameKey];
      }
      const existingSnapshot = loadedUsers[existingUserKey];
      const shouldPersistUsersIndex =
        !existingSnapshot ||
        existingSnapshot.username !== normalizedUser.username ||
        String(existingSnapshot.password || '') !== normalizedUser.password ||
        existingSnapshot.user_uuid !== normalizedUser.user_uuid ||
        existingSnapshot.review_json_key !== normalizedUser.review_json_key ||
        Number(existingSnapshot.review_count || 0) !== normalizedUser.review_count ||
        existingUserKey !== usernameKey;

      if (shouldPersistUsersIndex) {
        await saveJsonArray(getUsersKey(), syncedUsersIndex);
      }
      setUsersIndex(syncedUsersIndex);

      setUsername(trimmedUsername);
      setUserUuid(nextUser.user_uuid);
      setIsStarted(true);
    } catch (loadError) {
      console.error(loadError);
      setError('We could not load the user review record. Please try again.');
    } finally {
      setIsLoadingRecord(false);
    }
  };

  const persistReview = async () => {
    if (!userUuid || !username) {
      setError('Please start the review session first.');
      return;
    }

    // if (!currentAnalysis?.originalImageUrl || !currentAnalysis?.alternatives?.length) {
    //   setError('Please wait for the analysis to finish before saving.');
    //   return;
    // }

    const ratingError = validateRatings(imageRating, assessmentRating);
    if (ratingError) {
      setError(ratingError);
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const usernameKey = normalizeUsernameKey(username);
      const currentUserRecord =
        usersIndex[usernameKey] ||
        findRegistryUser(usersIndex, username)?.entry ||
        {
          username,
          password: '',
          user_uuid: userUuid,
          review_json_key: getJsonKey(userUuid),
          review_count: reviewCount,
        };
      const nextReviewEntry = createReviewPayload({
        user_uuid: currentUserRecord.user_uuid || userUuid,
        username,
        image_rating: imageRating,
        assessment_rating: assessmentRating,
        image_feedback: imageFeedback.trim(),
        assessment_feedback: assessmentFeedback.trim(),
      });

      const nextRecord = [...reviewEntries, nextReviewEntry];
      const nextUsersIndex = {
        ...usersIndex,
        [usernameKey]: {
          ...currentUserRecord,
          username,
          user_uuid: currentUserRecord.user_uuid || userUuid,
          review_json_key: getJsonKey(currentUserRecord.user_uuid || userUuid),
          review_count: nextRecord.length,
          updated_at: new Date().toISOString(),
        },
      };

      await saveJsonArray(getJsonKey(userUuid), nextRecord);
      await saveJsonArray(getUsersKey(), nextUsersIndex);
      setUsersIndex(nextUsersIndex);
      setReviewEntries(nextRecord);
      setReviewCount(nextRecord.length);
      setAnalysisData(null);
      setImageRating('');
      setAssessmentRating('');
      setImageFeedback('');
      setAssessmentFeedback('');
      setRoundKey((value) => value + 1);
      resetEditingState();
    } catch (saveError) {
      console.error('Unable to persist review', saveError);
      setError('We could not save the review to S3. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const startEditingReview = (entry) => {
    const analysisId = entry?.analysis_id || '';
    if (!analysisId) return;

    setEditingAnalysisId(analysisId);
    setEditingDraft({
      imageRating: String(entry?.image_rating || ''),
      assessmentRating: String(entry?.assessment_rating || ''),
      imageFeedback: String(entry?.image_feedback || ''),
      assessmentFeedback: String(entry?.assessment_feedback || ''),
    });
    setError('');
  };

  const cancelEditingReview = () => {
    resetEditingState();
  };

  const saveEditedReview = async (entry) => {
    if (!userUuid || !username) {
      setError('Please start the review session first.');
      return;
    }

    if (!entry?.analysis_id) return;

    const nextImageRating = editingDraft?.imageRating ?? '';
    const nextAssessmentRating = editingDraft?.assessmentRating ?? '';
    const ratingError = validateRatings(nextImageRating, nextAssessmentRating);
    if (ratingError) {
      setError(ratingError);
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const usernameKey = normalizeUsernameKey(username);
      const currentUserRecord =
        usersIndex[usernameKey] ||
        findRegistryUser(usersIndex, username)?.entry ||
        {
          username,
          password: '',
          user_uuid: userUuid,
          review_json_key: getJsonKey(userUuid),
          review_count: reviewCount,
        };

      const updatedEntry = createReviewPayload({
        ...entry,
        user_uuid: currentUserRecord.user_uuid || userUuid,
        username,
        image_rating: nextImageRating,
        assessment_rating: nextAssessmentRating,
        image_feedback: String(editingDraft?.imageFeedback || '').trim(),
        assessment_feedback: String(editingDraft?.assessmentFeedback || '').trim(),
        updated_at: new Date().toISOString(),
      });

      const nextRecord = reviewEntries.map((existingEntry) => (
        existingEntry?.analysis_id === entry.analysis_id ? updatedEntry : existingEntry
      ));

      const nextUsersIndex = {
        ...usersIndex,
        [usernameKey]: {
          ...currentUserRecord,
          username,
          user_uuid: currentUserRecord.user_uuid || userUuid,
          review_json_key: getJsonKey(currentUserRecord.user_uuid || userUuid),
          review_count: nextRecord.length,
          updated_at: new Date().toISOString(),
        },
      };

      await saveJsonArray(getJsonKey(userUuid), nextRecord);
      await saveJsonArray(getUsersKey(), nextUsersIndex);
      setUsersIndex(nextUsersIndex);
      setReviewEntries(nextRecord);
      setReviewCount(nextRecord.length);
      resetEditingState();
    } catch (saveError) {
      console.error('Unable to update review', saveError);
      setError('We could not update the review in S3. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const skipReview = () => {
    setAnalysisData(null);
    setImageRating('');
    setAssessmentRating('');
    setImageFeedback('');
    setAssessmentFeedback('');
    setRoundKey((value) => value + 1);
  };

  const stopSession = async () => {
    if (analysisData?.originalImage && (imageRating || assessmentRating)) {
      await persistReview();
    }
    setIsStarted(false);
  };

  const handleAnalysisReady = (payload) => {
    setAnalysisData(
      payload
        ? {
          originalImage: payload.originalImage || '',
          originalImageUrl: payload.originalImageUrl || payload.originalImage || '',
          alternatives: Array.isArray(payload.alternatives) ? payload.alternatives : [],
          triage: payload.triage || null,
        }
        : null,
    );
    setImageRating('');
    setAssessmentRating('');
    setImageFeedback('');
    setAssessmentFeedback('');
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 px-4 py-6 text-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[110rem] space-y-6">
          <header className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="space-y-2">
                <p className="text-s font-black uppercase tracking-[0.3em] text-cyan-700">Dental review (V2.0)</p>
                <h1 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                  Welcome{username ? `, ${username}` : ''}
                </h1>
                <p className="max-w-3xl text-m text-slate-600">
                  You have completed <span className="font-black text-slate-950">{reviewCount}</span> review{reviewCount === 1 ? '' : 's'} so far.
                </p>
              </div>
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-3 text-m text-slate-700">
                <div className="font-bold text-slate-950">{username || 'Welcome, user'}</div>
                <div className="mt-1 text-s text-slate-500">Completed reviews: {reviewCount}</div>
              </div>
            </div>
          </header>

          {!isStarted && (
            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 text-slate-900 shadow-xl">
              <div className="space-y-4">
                <div>
                  <p className="text-s font-black uppercase tracking-[0.25em] text-cyan-700">Start here</p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight">Enter your username and password</h2>
                  <p className="mt-2 text-m text-slate-600">We’ll load your review history and bring you back to the same session.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                  <input
                    type="text"
                    value={usernameInput}
                    onChange={(event) => setUsernameInput(event.target.value)}
                    placeholder="Username"
                    className="w-full rounded-[1.5rem] border border-slate-300 bg-slate-50 px-4 py-3 text-base outline-none transition focus:border-cyan-500 focus:bg-white"
                  />
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(event) => setPasswordInput(event.target.value)}
                    placeholder="Password"
                    className="w-full rounded-[1.5rem] border border-slate-300 bg-slate-50 px-4 py-3 text-base outline-none transition focus:border-cyan-500 focus:bg-white"
                  />
                  <button
                    type="button"
                    onClick={startReview}
                    disabled={isLoadingRecord}
                    className="inline-flex items-center justify-center gap-2 rounded-[1.5rem] bg-slate-950 px-6 py-3 font-black text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    {isLoadingRecord ? <RefreshCw size={18} className="animate-spin" /> : <UploadCloud size={18} />}
                    {isLoadingRecord ? 'Loading...' : 'Start review'}
                  </button>
                </div>
                {error && <p className="text-m font-semibold text-rose-600">{error}</p>}
              </div>
            </section>
          )}

          {isStarted && (
            <div className="space-y-6 ">
              <div className="inline-flex flex-1 w-full rounded-[1.5rem] border border-slate-200 bg-slate-50 p-2 shadow-sm bg-white">
                <button
                  type="button"
                  onClick={() => setActiveTab('main')}
                  className={`flex-1 rounded-[1.2rem] px-4 py-3 text-sm font-black transition ${activeTab === 'main'
                    ? 'bg-slate-950 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-950'
                    }`}
                >
                  Review
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('history')}
                  className={`flex-1 rounded-[1.2rem] px-4 py-3 text-sm font-black transition ${activeTab === 'history'
                    ? 'bg-slate-950 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-950'
                    }`}
                >
                  History
                </button>
              </div>

              {activeTab === 'main' ? (
                <div className="space-y-6">
                  <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-4 shadow-xl">
                    <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-3 sm:p-4">
                      <FalconSmileAppointmentProposal
                        key={roundKey}
                        config={{ apiKey, parentOrigin }}
                        reviewMode
                        onAnalysisReady={handleAnalysisReady}
                      />
                    </div>
                  </section>

                  <section className="rounded-[2rem] border border-slate-200 bg-white p-6 text-slate-900 shadow-xl">
                    <h2 className="mt-2 text-2xl font-black tracking-tight">Dentist review</h2>
                    <div className="mt-6 space-y-5">
                      <div className="grid gap-5 lg:grid-cols-2">
                        <label className="block">
                          <span className="text-m font-semibold text-slate-700">Rate the images that have been generated</span>
                          {renderStars(imageRating, setImageRating)}
                        </label>

                        <label className="block">
                          <span className="text-m font-semibold text-slate-700">Rate the assessment that has been generated</span>
                          {renderStars(assessmentRating, setAssessmentRating)}
                        </label>
                      </div>

                      <div className="grid gap-5 lg:grid-cols-2">
                        <label className="block">
                          <span className="text-m font-semibold text-slate-700">Image feedback</span>
                          <textarea
                            value={imageFeedback}
                            onChange={(event) => setImageFeedback(event.target.value)}
                            rows={5}
                            className="mt-2 w-full rounded-[1.5rem] border border-slate-300 bg-slate-50 px-4 py-3 text-m outline-none transition focus:border-cyan-500 focus:bg-white"
                            placeholder="How could the generated image be improved?"
                          />
                        </label>

                        <label className="block">
                          <span className="text-m font-semibold text-slate-700">Assessment feedback</span>
                          <textarea
                            value={assessmentFeedback}
                            onChange={(event) => setAssessmentFeedback(event.target.value)}
                            rows={5}
                            className="mt-2 w-full rounded-[1.5rem] border border-slate-300 bg-slate-50 px-4 py-3 text-m outline-none transition focus:border-cyan-500 focus:bg-white"
                            placeholder="How could the assessment be improved?"
                          />
                        </label>
                      </div>

                      {error && (
                        <div className="rounded-[1.35rem] border border-rose-200 bg-rose-50 px-4 py-3 text-m font-semibold text-rose-700">
                          {error}
                        </div>
                      )}
                    </div>

                    <div className="mt-8 border-t border-slate-200 pt-5">
                      <p className="text-m text-slate-600">Next saves the review. Skip clears this smile. Stop ends the session.</p>
                      <div className="mt-4 flex flex-wrap justify-end gap-3">
                        <button
                          type="button"
                          onClick={skipReview}
                          className="inline-flex items-center justify-center gap-2 rounded-[1.35rem] border border-slate-300 bg-slate-50 px-5 py-3 font-black text-slate-900 transition hover:bg-slate-100"
                        >
                          <SkipForward size={18} />
                          Skip
                        </button>
                        <button
                          type="button"
                          onClick={stopSession}
                          className="inline-flex items-center justify-center gap-2 rounded-[1.35rem] border border-rose-300 bg-rose-50 px-5 py-3 font-black text-rose-700 transition hover:bg-rose-100"
                        >
                          <Square size={18} />
                          Stop
                        </button>
                        <button
                          type="button"
                          onClick={persistReview}
                          disabled={isSaving}
                          className="inline-flex items-center justify-center gap-2 rounded-[1.35rem] bg-cyan-500 px-5 py-3 font-black text-white transition hover:bg-cyan-600 disabled:cursor-not-allowed disabled:bg-cyan-300 disabled:text-white/80"
                        >
                          {isSaving ? <RefreshCw size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                          {isSaving ? 'Saving...' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </section>
                </div>
              ) : (
                <section className="rounded-[2rem] border border-slate-200 p-6 text-slate-900 shadow-xl">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-s font-black uppercase tracking-[0.25em] text-cyan-700">History</p>
                      <h2 className="mt-2 text-2xl font-black tracking-tight">Reviewed items</h2>
                      <p className="mt-2 max-w-3xl text-m text-slate-600">
                        Each card shows the review, the three photographs, the star ratings, and the feedback together.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    {historyDisplayEntries.length === 0 ? (
                      <div className="rounded-[1.5rem] border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-slate-500">
                        No reviews have been saved yet.
                      </div>
                    ) : (
                      historyDisplayEntries.map((entry, index) => {
                        const originalImage = resolveSavedImageUrl(entry.original_image_url);
                        const altImage1 = resolveSavedImageUrl(entry.alternative_image_url_1);
                        const altImage2 = resolveSavedImageUrl(entry.alternative_image_url_2);
                        const isEditingThis = editingAnalysisId === entry.analysis_id && editingDraft;
                        const viewerImages = [
                          { key: 'original', label: 'Original', src: originalImage },
                          { key: 'alternative-1', label: 'Alternative 1', src: altImage1 },
                          { key: 'alternative-2', label: 'Alternative 2', src: altImage2 },
                        ];
                        const carouselKey = entry.analysis_id || `history-${index}`;
                        const selectedCarouselKey = mobileCarouselKeys[carouselKey] || getFirstAvailableImageKey(viewerImages);
                        const selectedCarouselImage = getSelectedCarouselImage(viewerImages, selectedCarouselKey);
                        const assessment = getEntryAssessment(entry);
                        const assessmentSpecialists = getAssessmentSpecialists(assessment);

                        return (
                          <div key={entry.analysis_id || index} className="rounded-[1.4rem] border border-slate-200 bg-white p-4 mb-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <p className="text-s font-black uppercase tracking-[0.25em] text-cyan-700">Review {index + 1}</p>
                                <h3 className="mt-1 text-lg font-black text-slate-950">
                                  {username || 'Dentist'} review
                                </h3>
                                <p className="mt-1 text-sm text-slate-500">
                                  {new Date(entry.created_at || Date.now()).toLocaleString()}
                                </p>
                              </div>
                              <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                                <div>Analysis ID: {entry.analysis_id || 'N/A'}</div>
                                <div>Image rating: {entry.image_rating || 0}/5</div>
                                <div>Assessment rating: {entry.assessment_rating || 0}/5</div>
                              </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                              {!isEditingThis ? (
                                <button
                                  type="button"
                                  onClick={() => startEditingReview(entry)}
                                  className="inline-flex items-center justify-center rounded-[1.2rem] border border-slate-300 bg-slate-50 px-4 py-2 text-sm font-black text-slate-900 transition hover:bg-slate-100"
                                >
                                  Edit review
                                </button>
                              ) : (
                                <span className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-cyan-700">
                                  Editing
                                </span>
                              )}
                            </div>

                            <div className="mt-5">
                              <p className="text-sm font-black uppercase tracking-[0.2em] text-slate-500">Reviewed photographs</p>
                              <div className="mt-3 lg:hidden">
                                <div className="overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white">
                                  <div className="border-b border-slate-200 px-4 py-3 text-sm font-black uppercase tracking-[0.2em] text-slate-500">
                                    {selectedCarouselImage?.label || 'Photograph'}
                                  </div>
                                  <div className="relative bg-slate-100">
                                    {selectedCarouselImage ? (
                                      <button
                                        type="button"
                                        onClick={() => openViewer({ images: viewerImages, initialKey: selectedCarouselImage.key })}
                                        className="block w-full focus:outline-none focus:ring-4 focus:ring-cyan-500/30"
                                        aria-label={`Open ${selectedCarouselImage.label} image`}
                                      >
                                        <img
                                          src={selectedCarouselImage.src}
                                          alt={`${selectedCarouselImage.label} review`}
                                          loading="lazy"
                                          className="h-72 w-full object-contain"
                                        />
                                      </button>
                                    ) : (
                                      <div className="flex h-72 items-center justify-center px-4 text-sm text-slate-400">
                                        Not saved
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => stepMobileCarousel(carouselKey, viewerImages, -1)}
                                      className="absolute left-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-950 shadow-lg"
                                      aria-label="Previous image"
                                    >
                                      <ChevronLeft size={22} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => stepMobileCarousel(carouselKey, viewerImages, 1)}
                                      className="absolute right-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-950 shadow-lg"
                                      aria-label="Next image"
                                    >
                                      <ChevronRight size={22} />
                                    </button>
                                  </div>
                                </div>

                                <div className="mt-3 grid grid-cols-3 gap-3">
                                  {viewerImages.map((imageItem) => (
                                    <button
                                      key={`${carouselKey}-thumb-${imageItem.key}`}
                                      type="button"
                                      onClick={() => imageItem.src && updateMobileCarouselKey(carouselKey, imageItem.key)}
                                      disabled={!imageItem.src}
                                      className={`overflow-hidden rounded-[0.85rem] border-2 bg-slate-100 transition ${selectedCarouselImage?.key === imageItem.key
                                        ? 'border-cyan-500'
                                        : 'border-slate-200'
                                        } disabled:opacity-50`}
                                      aria-label={`Show ${imageItem.label}`}
                                    >
                                      {imageItem.src ? (
                                        <img
                                          src={imageItem.src}
                                          alt={`${imageItem.label} thumbnail`}
                                          loading="lazy"
                                          className="h-20 w-full object-contain"
                                        />
                                      ) : (
                                        <div className="flex h-20 items-center justify-center px-2 text-xs text-slate-400">
                                          Empty
                                        </div>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="mt-3 hidden gap-4 lg:grid lg:grid-cols-3">
                                {[
                                  { key: 'original', label: 'Original', src: originalImage },
                                  { key: 'alternative-1', label: 'Alternative 1', src: altImage1 },
                                  { key: 'alternative-2', label: 'Alternative 2', src: altImage2 },
                                ].map((imageItem) => (
                                  <div
                                    key={`${entry.analysis_id || index}-${imageItem.label}`}
                                    className="overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white"
                                  >
                                    <div className="border-b border-slate-200 px-4 py-3 text-sm font-black uppercase tracking-[0.2em] text-slate-500">
                                      {imageItem.label}
                                    </div>
                                    <div className="bg-slate-100">
                                      {imageItem.src ? (
                                        <button
                                          type="button"
                                          onClick={() => openViewer({ images: viewerImages, initialKey: imageItem.key })}
                                          className="block w-full focus:outline-none focus:ring-4 focus:ring-cyan-500/30"
                                          aria-label={`Open ${imageItem.label} image`}
                                        >
                                          <img
                                            src={imageItem.src}
                                            alt={`${imageItem.label} review`}
                                            loading="lazy"
                                            className="h-56 w-full object-contain transition hover:scale-[1.01]"
                                          />
                                        </button>
                                      ) : (
                                        <div className="flex h-56 items-center justify-center px-4 text-sm text-slate-400">
                                          Not saved
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="mt-4 rounded-[1.2rem] border border-slate-200 bg-slate-50 p-4">
                              <p className="text-sm font-black uppercase tracking-[0.2em] text-slate-500">Generated assessment</p>
                              {assessmentSpecialists.length > 0 ? (
                                <div className="mt-3 grid gap-3 min-[1200px]:grid-cols-2">
                                  {assessmentSpecialists.map((item, specialistIndex) => (
                                    <div
                                      key={`${entry.analysis_id || index}-assessment-${item?.name || specialistIndex}`}
                                      className="rounded-[1rem] border border-slate-200 bg-white p-4"
                                    >
                                      <p className="text-sm font-black text-slate-950">
                                        {item?.name ? `${item.name} Care` : `Finding ${specialistIndex + 1}`}
                                      </p>
                                      <p className="mt-2 text-sm leading-6 text-slate-600">
                                        {item?.reason || 'No assessment note returned.'}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="mt-3 text-sm leading-6 text-slate-500">
                                  No generated assessment was saved for this history item.
                                </p>
                              )}
                            </div>

                            <div className="mt-4 rounded-[1.2rem] border border-slate-200 bg-slate-50 p-4">
                              <p className="text-sm font-black uppercase tracking-[0.2em] text-slate-500">Feedback</p>
                              {!isEditingThis ? (
                                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                                  <div className="rounded-[1rem] border border-slate-200 bg-white p-4">
                                    <p className="text-sm font-black uppercase tracking-[0.15em] text-slate-500">Image feedback</p>
                                    {renderStaticStars(entry.image_rating)}
                                    <p className="mt-3 text-sm text-slate-600">{entry.image_feedback || 'No image feedback provided.'}</p>
                                  </div>
                                  <div className="rounded-[1rem] border border-slate-200 bg-white p-4">
                                    <p className="text-sm font-black uppercase tracking-[0.15em] text-slate-500">Assessment feedback</p>
                                    {renderStaticStars(entry.assessment_rating)}
                                    <p className="mt-3 text-sm text-slate-600">{entry.assessment_feedback || 'No assessment feedback provided.'}</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-3 space-y-4">
                                  <div className="grid gap-5 lg:grid-cols-2">
                                    <label className="block rounded-[1rem] border border-slate-200 bg-white p-4">
                                      <span className="text-m font-semibold text-slate-700">Edit image rating</span>
                                      {renderStars(editingDraft.imageRating, (value) => {
                                        setEditingDraft((draft) => ({ ...draft, imageRating: value }));
                                      })}
                                    </label>

                                    <label className="block rounded-[1rem] border border-slate-200 bg-white p-4">
                                      <span className="text-m font-semibold text-slate-700">Edit assessment rating</span>
                                      {renderStars(editingDraft.assessmentRating, (value) => {
                                        setEditingDraft((draft) => ({ ...draft, assessmentRating: value }));
                                      })}
                                    </label>
                                  </div>

                                  <div className="grid gap-5 lg:grid-cols-2">
                                    <label className="block rounded-[1rem] border border-slate-200 bg-white p-4">
                                      <span className="text-m font-semibold text-slate-700">Edit image feedback</span>
                                      <textarea
                                        value={editingDraft.imageFeedback}
                                        onChange={(event) => {
                                          const value = event.target.value;
                                          setEditingDraft((draft) => ({ ...draft, imageFeedback: value }));
                                        }}
                                        rows={4}
                                        className="mt-2 w-full rounded-[1.25rem] border border-slate-300 bg-slate-50 px-4 py-3 text-m outline-none transition focus:border-cyan-500 focus:bg-white"
                                      />
                                    </label>

                                    <label className="block rounded-[1rem] border border-slate-200 bg-white p-4">
                                      <span className="text-m font-semibold text-slate-700">Edit assessment feedback</span>
                                      <textarea
                                        value={editingDraft.assessmentFeedback}
                                        onChange={(event) => {
                                          const value = event.target.value;
                                          setEditingDraft((draft) => ({ ...draft, assessmentFeedback: value }));
                                        }}
                                        rows={4}
                                        className="mt-2 w-full rounded-[1.25rem] border border-slate-300 bg-slate-50 px-4 py-3 text-m outline-none transition focus:border-cyan-500 focus:bg-white"
                                      />
                                    </label>
                                  </div>

                                  <div className="flex flex-wrap justify-end gap-3 pt-2">
                                    <button
                                      type="button"
                                      onClick={cancelEditingReview}
                                      className="inline-flex items-center justify-center rounded-[1.2rem] border border-slate-300 bg-white px-4 py-2 text-sm font-black text-slate-900 transition hover:bg-slate-50"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => saveEditedReview(entry)}
                                      disabled={isSaving}
                                      className="inline-flex items-center justify-center rounded-[1.2rem] bg-cyan-500 px-4 py-2 text-sm font-black text-white transition hover:bg-cyan-600 disabled:cursor-not-allowed disabled:bg-cyan-300"
                                    >
                                      {isSaving ? 'Saving...' : 'Save changes'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={historyLoadMoreRef} className="h-10" aria-hidden="true" />
                    {visibleHistoryCount < sortedReviewedEntries.length && (
                      <div className="text-center text-sm font-semibold text-slate-500">
                        Loading more history...
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {viewerImage && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-xl"
          onClick={closeViewer}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
        >
          <div
            className="relative flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-black/35 shadow-[0_30px_80px_rgba(0,0,0,0.55)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeViewer}
              className="absolute right-4 top-4 z-10 rounded-full bg-white/90 px-4 py-2 text-sm font-black text-slate-950 shadow-lg transition hover:bg-white"
              aria-label="Close image preview"
            >
              Close
            </button>

            <div className="border-b border-white/10 bg-white/5 px-4 py-4 sm:px-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.25em] text-cyan-200">Image preview</p>
                  <h2 className="mt-1 text-2xl font-black text-white">Compare the smile images</h2>
                  <p className="mt-1 max-w-2xl text-sm text-slate-200">
                    Switch between a single image, two images side by side, or all three images at once.
                  </p>
                </div>

                <div className="inline-flex flex-wrap gap-2 rounded-[1.1rem] bg-white/10 p-2">
                  {['single', 'two', 'three'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setViewerMode(mode)}
                      className={`rounded-[0.9rem] px-4 py-2 text-sm font-black capitalize transition ${viewerImage.mode === mode
                        ? 'bg-white text-slate-950 shadow-lg'
                        : 'bg-transparent text-white hover:bg-white/10'
                        }`}
                    >
                      {mode === 'single' ? '1 image' : mode === 'two' ? '2 images' : 'All Images'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4 sm:p-5">
              {viewerImage.mode === 'single' && (
                <div className="space-y-4">
                  {renderViewerPicker('Single image', viewerImage.singleKey, (key) => updateViewerSlot('single', key))}
                  <div className="flex min-h-[420px] items-center justify-center rounded-[1.5rem] border border-white/10 bg-black/25 p-4">
                    {(() => {
                      const selectedImage = getViewerItem(viewerImage.singleKey);
                      return selectedImage ? (
                        <img
                          src={selectedImage.src}
                          alt={selectedImage.label}
                          className="max-h-[72vh] max-w-full object-contain"
                        />
                      ) : (
                        <div className="text-sm font-semibold text-slate-300">No image available.</div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {viewerImage.mode === 'two' && (
                <div className="space-y-4">
                  <div className="grid gap-4 xl:grid-cols-2">
                    {renderViewerPicker('Left image', viewerImage.leftKey, (key) => updateViewerSlot('left', key))}
                    {renderViewerPicker('Right image', viewerImage.rightKey, (key) => updateViewerSlot('right', key))}
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    <div className="flex min-h-[360px] items-center justify-center rounded-[1.5rem] border border-white/10 bg-black/25 p-4">
                      {(() => {
                        const selectedImage = getViewerItem(viewerImage.leftKey);
                        return selectedImage ? (
                          <img
                            src={selectedImage.src}
                            alt={selectedImage.label}
                            className="max-h-[65vh] max-w-full object-contain"
                          />
                        ) : (
                          <div className="text-sm font-semibold text-slate-300">No image available.</div>
                        );
                      })()}
                    </div>

                    <div className="flex min-h-[360px] items-center justify-center rounded-[1.5rem] border border-white/10 bg-black/25 p-4">
                      {(() => {
                        const selectedImage = getViewerItem(viewerImage.rightKey);
                        return selectedImage ? (
                          <img
                            src={selectedImage.src}
                            alt={selectedImage.label}
                            className="max-h-[65vh] max-w-full object-contain"
                          />
                        ) : (
                          <div className="text-sm font-semibold text-slate-300">No image available.</div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {viewerImage.mode === 'three' && (
                <div className="space-y-4">
                  <div className="grid gap-4 xl:grid-cols-3">
                    {viewerImage.images.map((item) => (
                      <div
                        key={item.key}
                        className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-black/25"
                      >
                        <div className="border-b border-white/10 px-4 py-3 text-sm font-black uppercase tracking-[0.2em] text-cyan-200">
                          {item.label}
                        </div>
                        <div className="flex min-h-[360px] items-center justify-center p-4">
                          <img
                            src={item.src}
                            alt={item.label}
                            className="max-h-[65vh] max-w-full object-contain"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FalconSmileReview;
